import React, { Fragment, useEffect, useState } from "react";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import SiteHeader from "../../components/SiteHeader";
import SiteFooter from "../../components/SiteFooter";
import ArgusRoleModal from "../../components/ArgusRoleModal";
import TradeShowModal from "../../components/TradeShowModal";
import { Backdrop, CircularProgress, Typography } from "@mui/material";
import { ApiCallStatus } from "../../utils/constants";
import { fetchUserPageContentData } from "../PageFunctions/pageContentFunctions";
import { fetchCategoriesAndTypes } from "../PageFunctions/productFunctions";
import AddedToCart from "../../components/AddedToCart";
import {
  activateUserVehicle,
  createUserVehicle,
  removeUserVehicle,
} from "../PageFunctions/userVehicleFunctions";
import Announcements from "../../components/Announcements";
import { useNavigate } from "react-router-dom";
import { isNil } from "lodash";

const PageWrapper = (props) => {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useArgusAuth();
  const [argusRoleModalOpen, setArgusRoleModalOpen] = useState(false);
  const [userPageContent, setUserPageContent] = useState(null);
  const [genericDataLoaded, setGenericDataLoaded] = useState(false);
  const [categoriesAndTypes, setCategoriesAndTypes] = useState([]);
  const [accountDropdownOpen, setAccountDropdownOpen] = React.useState(false);

  const [
    fetchCategoriesAndTypesCallStatus,
    setFetchCategoriesAndTypesCallStatus,
  ] = useState(ApiCallStatus.NotStarted);
  const [fetchUserPageContentCallStatus, setFetchUserPageContentCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [createUserVehicleCallStatus, setCreateUserVehicleCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [removeUserVehicleCallStatus, setRemoveUserVehicleCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [activateUserVehicleCallStatus, setActivateUserVehicleCallStatus] =
    useState(ApiCallStatus.NotStarted);

  const fetchCategoriesAndTypesAsync = async () => {
    await fetchCategoriesAndTypes({
      setCategoriesAndTypesData: setCategoriesAndTypes,
      setStatusInProgress: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchCategoriesAndTypesCallStatus(ApiCallStatus.Error),
    });
  };

  const createUserVehicleAsync = async ({
    vehicleToSave,
    setStatusSuccess,
    setStatusInProgress,
    setStatusFailed,
    setStatusError,
  }) => {
    await createUserVehicle({
      vehicleYearId: vehicleToSave.vehicleYearId,
      makeId: vehicleToSave.makeId,
      modelId: vehicleToSave.modelId,
      yearId: vehicleToSave.yearId,
      variantId: vehicleToSave.variantId,
      setStatusInProgress: () => setStatusInProgress(),
      setStatusSuccess: () => {
        setStatusSuccess();
        setCreateUserVehicleCallStatus(ApiCallStatus.Succeeded);
      },
      setStatusFailed: () => setStatusFailed(),
      setStatusError: () => setStatusError(),
    });
  };

  const removeUserVehicleAsync = async (userVehicleId) => {
    await removeUserVehicle({
      userVehicleId,
      setStatusInProgress: () =>
        setRemoveUserVehicleCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setRemoveUserVehicleCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setRemoveUserVehicleCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setRemoveUserVehicleCallStatus(ApiCallStatus.Error),
    });
  };

  const activateUserVehicleAsync = async (userVehicleId) => {
    await activateUserVehicle({
      userVehicleId,
      setStatusInProgress: () =>
        setActivateUserVehicleCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setActivateUserVehicleCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setActivateUserVehicleCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setActivateUserVehicleCallStatus(ApiCallStatus.Error),
    });
  };

  const callFetchUserPageContent = async () => {
    setFetchUserPageContentCallStatus(ApiCallStatus.InProgress);
    await fetchUserPageContentData({
      setPageContent: setUserPageContent,
      setStatusInProgress: () =>
        setFetchUserPageContentCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchUserPageContentCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchUserPageContentCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchUserPageContentCallStatus(ApiCallStatus.Error),
    });
  };

  const refreshPage = () => {
    navigate(0);
  };

  const handleAccountDropdownClick = () => {
    setAccountDropdownOpen(!accountDropdownOpen);
  };

  useEffect(() => {
    document.querySelector("body").scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const fetchUserPageContentData = async () => {
      await callFetchUserPageContent();
    };

    if (!isLoading) {
      fetchUserPageContentData();
    }
  }, [isLoading]);

  useEffect(() => {
    if (fetchUserPageContentCallStatus === ApiCallStatus.Succeeded) {
      setGenericDataLoaded(true);
    }
  }, [fetchUserPageContentCallStatus]);

  useEffect(() => {
    if (
      createUserVehicleCallStatus === ApiCallStatus.Succeeded ||
      removeUserVehicleCallStatus === ApiCallStatus.Succeeded ||
      activateUserVehicleCallStatus === ApiCallStatus.Succeeded
    ) {
      setGenericDataLoaded(false);
      callFetchUserPageContent();
    }
  }, [
    createUserVehicleCallStatus,
    removeUserVehicleCallStatus,
    activateUserVehicleCallStatus,
  ]);

  useEffect(() => {
    if (genericDataLoaded === true && categoriesAndTypes.length === 0) {
      fetchCategoriesAndTypesAsync();
    }
  }, [genericDataLoaded, categoriesAndTypes]);

  useEffect(() => {
    if (genericDataLoaded && !!userPageContent?.userId && isAuthenticated) {
      setArgusRoleModalOpen(isNil(userPageContent.accountType));
    }
  }, [userPageContent, genericDataLoaded]);

  const allLoadingStates = [
    fetchUserPageContentCallStatus,
    fetchCategoriesAndTypesCallStatus,
    createUserVehicleCallStatus,
    removeUserVehicleCallStatus,
    activateUserVehicleCallStatus,
  ];

  const pageIsLoading =
    allLoadingStates.includes(ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;

  let additionalProps;
  let childrenWithProps;

  if (
    genericDataLoaded === true &&
    !!userPageContent &&
    !!categoriesAndTypes.length > 0
  ) {
    additionalProps = {
      genericDataLoaded,
      genericPageContent: userPageContent,
      categoriesAndTypes,
    };

    childrenWithProps = React.Children.map(props.children, (child) => {
      return React.cloneElement(child, { ...props, ...additionalProps });
    });
  }

  const hideFooter = props.hideFooter === true;
  // Standalone flows (e.g. the trade-application wizard) render their own minimal branded chrome and
  // don't want the storefront header/footer or the account/cart/announcement popups layered on top —
  // they still need PageWrapper for the genericPageContent fetch, just not any of its UI.
  const minimal = props.minimal === true;

  return (
    <Fragment>
      {!!pageIsLoading && (
        <Typography variant="body2">Please wait...</Typography>
      )}
      {!pageIsLoading && !!additionalProps && childrenWithProps && (
        <>
          {!minimal && (
            <>
              {/* <SiteTopbar /> */}
              <SiteHeader
                genericPageContent={userPageContent}
                genericDataLoaded={genericDataLoaded}
                categoriesAndTypes={categoriesAndTypes}
                activateUserVehicleAsync={activateUserVehicleAsync}
                createUserVehicleAsync={createUserVehicleAsync}
                removeUserVehicleAsync={removeUserVehicleAsync}
                handleAccountDropdownClick={handleAccountDropdownClick}
                accountDropdownOpen={accountDropdownOpen}
                setAccountDropdownOpen={setAccountDropdownOpen}
              />
              <TradeShowModal />
              {!!argusRoleModalOpen && isAuthenticated === true && (
                <ArgusRoleModal
                  open={!!argusRoleModalOpen}
                  setClose={() => setArgusRoleModalOpen(false)}
                  refreshPage={refreshPage}
                />
              )}
              <Announcements genericDataLoaded={genericDataLoaded} />
              <AddedToCart />
            </>
          )}
          {childrenWithProps}

          {!minimal && !hideFooter && <SiteFooter />}
        </>
      )}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={pageIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Fragment>
  );
};
export default PageWrapper;
